import { useState, useMemo, useCallback } from "react";
import EmptyState from "@/components/EmptyState";
import Table, { TableHeading, buildActionColumn, buildTagColumn } from "@/components/Table";
import { buildExpandableCountColumn } from "@/components/Table/buildExpandableCountColumn";
import NotesTable from "@/features/global/components/NotesTable";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { toFormat } from "@/lib/dateHelpers";
import { CcmsSuspension, CRMNote, NoteNotableValues } from "@/types";
import { DocumentNode } from "@apollo/client";
import AddSuspensionModal from "./AddSuspensionModal";
import EditSuspensionModal from "./EditSuspensionModal";
import DeleteSuspensionModal from "./DeleteSuspensionModal";

type SuspensionStatus = "Active" | "Ended" | "Future";

interface SuspensionHistoryTableProps {
  suspensions: CcmsSuspension[];
  loading?: boolean;
  canEdit?: boolean;
  canAddSuspension?: boolean;
  canManageSuspensions?: boolean;
  canManageSuspensionsRestricted?: boolean;
  pgaId?: string;
  personId?: string;
  refetchQueries?: DocumentNode[];
  isPersonActive?: boolean;
  currentUserId?: string;
  autoExpandSuspensionId?: string | null;
  highlightNoteId?: string | null;
  onSuspensionMutationSuccess: () => Promise<void> | void;
}

interface FormattedSuspension {
  id: string;
  code: string;
  startDate: string | null;
  endDate: string | null;
  type: string;
  reason: string;
  memo: string | null;
  notesCount: number;
  notes: CRMNote[];
  originalSuspension: CcmsSuspension;
  status: SuspensionStatus;
}

// Ethics violation code from CCMS
const ETHICS_VIOLATION_CODE = "COEV";

function formatTableData(suspensions: CcmsSuspension[]): FormattedSuspension[] {
  const today = new Date().toISOString().split("T")[0];

  return suspensions.map((suspension) => {
    const notes = suspension.notes || [];
    const startDate = suspension.startDate?.split("T")[0];
    const endDate = suspension.endDate?.split("T")[0];

    // Determine status: Future (not started), Active (started, not ended), Ended (has ended)
    let status: SuspensionStatus;
    if (!startDate || startDate > today) {
      status = "Future";
    } else if (!endDate || endDate > today) {
      status = "Active";
    } else {
      status = "Ended";
    }

    return {
      id: suspension.id,
      code: suspension.code,
      startDate: suspension.startDate ? toFormat(suspension.startDate) : null,
      endDate: suspension.endDate ? toFormat(suspension.endDate) : null,
      type: "Suspension",
      reason: suspension.reasonDescription ? `${suspension.reasonDescription} (${suspension.code})` : suspension.code,
      memo: suspension.memo || null,
      notesCount: notes.length,
      notes,
      originalSuspension: suspension,
      status
    };
  });
}

export default function SuspensionHistoryTable({
  suspensions,
  loading = false,
  canEdit = false,
  canAddSuspension = false,
  canManageSuspensions = false,
  canManageSuspensionsRestricted = false,
  pgaId,
  personId,
  refetchQueries,
  isPersonActive = false,
  currentUserId,
  autoExpandSuspensionId,
  highlightNoteId,
  onSuspensionMutationSuccess
}: SuspensionHistoryTableProps) {
  const [addSuspensionModalOpen, setAddSuspensionModalOpen] = useState(false);
  const [editSuspensionModalOpen, setEditSuspensionModalOpen] = useState(false);
  const [deleteSuspensionModalOpen, setDeleteSuspensionModalOpen] = useState(false);
  const [selectedSuspension, setSelectedSuspension] = useState<CcmsSuspension | null>(null);
  const [addNoteSuspensionId, setAddNoteSuspensionId] = useState<string | null>(null);
  const tableData = suspensions?.length ? formatTableData(suspensions) : [];

  // Resolve autoExpandSuspensionId (Postgres UUID or CCMS synthetic id) to the CCMS row id
  const [expandComplete, setExpandComplete] = useState(false);
  const resolvedExpandRowId = useMemo(() => {
    if (!autoExpandSuspensionId || expandComplete) return null;
    const byCcmsId = suspensions.find((s) => s.id === autoExpandSuspensionId);
    if (byCcmsId) return byCcmsId.id;
    const byPgId = suspensions.find((s) => s.membershipSuspensionId === autoExpandSuspensionId);
    return byPgId?.id ?? null;
  }, [autoExpandSuspensionId, suspensions, expandComplete]);
  const handleAutoExpandComplete = useCallback(() => setExpandComplete(true), []);

  // Calculate active suspensions (started and not ended yet)
  const activeSuspensions = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return suspensions.filter((suspension) => {
      const startDate = suspension.startDate?.split("T")[0];
      const endDate = suspension.endDate?.split("T")[0];

      if (!startDate) return false;
      const hasStarted = startDate <= today;
      const hasNotEnded = !endDate || endDate > today;

      return hasStarted && hasNotEnded;
    });
  }, [suspensions]);

  // Count of other active suspensions (excluding the one being edited)
  const otherActiveSuspensionCount = useMemo(() => {
    if (!selectedSuspension) return activeSuspensions.length;
    return activeSuspensions.filter((s) => s.id !== selectedSuspension.id).length;
  }, [activeSuspensions, selectedSuspension]);

  // Check if the selected suspension is currently active
  const isSelectedSuspensionActive = useMemo(() => {
    if (!selectedSuspension) return false;
    return activeSuspensions.some((s) => s.id === selectedSuspension.id);
  }, [activeSuspensions, selectedSuspension]);

  const { setMenu, Menu } = useTableRowMenu();

  // Determine if a suspension row should show actions based on permissions
  const canEditSuspension = (suspensionCode: string): boolean => {
    const isEthicsViolation = suspensionCode === ETHICS_VIOLATION_CODE;
    if (isEthicsViolation) {
      return canManageSuspensionsRestricted;
    }
    return canManageSuspensions;
  };

  const handleEditClick = (suspension: CcmsSuspension) => {
    setSelectedSuspension(suspension);
    setEditSuspensionModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditSuspensionModalOpen(false);
    setSelectedSuspension(null);
  };

  const handleDeleteClick = (suspension: CcmsSuspension) => {
    setSelectedSuspension(suspension);
    setDeleteSuspensionModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteSuspensionModalOpen(false);
    setSelectedSuspension(null);
  };

  const isSuspensionActive = (suspension: CcmsSuspension): boolean => {
    return activeSuspensions.some((s) => s.id === suspension.id);
  };

  // Check if any row can be edited (to determine if we need the actions column)
  const hasAnyEditableRows = useMemo(() => {
    return suspensions.some((suspension) => {
      const isEthicsViolation = suspension.code === ETHICS_VIOLATION_CODE;
      return isEthicsViolation ? canManageSuspensionsRestricted : canManageSuspensions;
    });
  }, [suspensions, canManageSuspensions, canManageSuspensionsRestricted]);

  const renderNotesPanel = (row: { original: FormattedSuspension }) => {
    const notes = row.original.notes || [];
    if (notes.length === 0) {
      return <></>;
    }

    return (
      <NotesTable
        variant="details-panel"
        data={notes}
        title={`Notes (${notes.length})`}
        highlightNoteId={highlightNoteId}
        noteEditing={{
          enabled: canEdit,
          notableId: row.original.id,
          notableType: NoteNotableValues.MEMBERSHIP_SUSPENSION,
          refetchQueries,
          pgaId: pgaId || "",
          objectType: "person"
        }}
      />
    );
  };

  return (
    <div id="suspension-history">
      <TableHeading
        heading="Suspension History"
        body="View suspension history including length and reason"
        actions={
          canAddSuspension
            ? [
                {
                  label: "Add Suspension",
                  icon: "add",
                  variant: "outlined" as const,
                  onClick: () => setAddSuspensionModalOpen(true),
                  testId: "add-suspension-button"
                }
              ]
            : []
        }
      />

      <Table
        data={tableData}
        loading={loading}
        getRowCanExpand={(row) => (row.original.notes?.length || 0) > 0}
        renderExpandedRow={({ row }) => renderNotesPanel(row)}
        columns={[
          {
            header: "Type",
            id: "type",
            accessorKey: "type",
            enableSorting: false
          },
          {
            header: "Reason",
            id: "reason",
            accessorKey: "reason",
            enableSorting: false,
            size: 300
          },
          {
            header: "Memo (Historical)",
            id: "memo",
            accessorKey: "memo",
            enableSorting: false,
            size: 300
          },
          {
            header: "Start Date",
            id: "startDate",
            accessorKey: "startDate",
            enableSorting: false
          },
          {
            header: "End Date",
            id: "endDate",
            accessorKey: "endDate",
            enableSorting: false
          },
          buildExpandableCountColumn<FormattedSuspension>({
            accessorKey: "notesCount",
            header: "Notes",
            getCount: (row) => row.notes?.length || 0,
            showExpandIcon: true,
            autoExpandRowId: resolvedExpandRowId,
            onAutoExpandComplete: handleAutoExpandComplete,
            size: 80
          }),
          buildTagColumn<FormattedSuspension>({
            accessorKey: "status",
            header: "Status",
            getTagLabel: (row) => row.status,
            getTagType: (row) => {
              switch (row.status) {
                case "Active":
                  return "warn";
                case "Future":
                  return "information";
                default:
                  return "surface";
              }
            },
            size: 100
          }),
          ...(hasAnyEditableRows || canEdit
            ? [
                buildActionColumn<FormattedSuspension>({
                  getActions: (row) => {
                    const actions: Action[] = [];

                    if (canEdit) {
                      actions.push({
                        text: "Add Note",
                        onClick: () => setAddNoteSuspensionId(row.id)
                      });
                    }

                    if (canEditSuspension(row.code)) {
                      actions.push({
                        text: "Edit Suspension",
                        onClick: () => handleEditClick(row.originalSuspension)
                      });

                      if (canManageSuspensionsRestricted && !isSuspensionActive(row.originalSuspension)) {
                        actions.push({
                          text: "Delete Suspension",
                          iconName: "delete",
                          destructive: true,
                          onClick: () => handleDeleteClick(row.originalSuspension)
                        });
                      }
                    }

                    return actions;
                  },
                  actionId,
                  setMenu
                })
              ]
            : [])
        ]}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Suspensions"
            caption="This person does not have any suspension history"
            iconName="account_circle_off"
          />
        )}
        data-testid="suspension-history-table"
      />

      {canAddSuspension && personId && (
        <AddSuspensionModal
          open={addSuspensionModalOpen}
          onClose={() => setAddSuspensionModalOpen(false)}
          personId={personId}
          pgaId={pgaId || ""}
          refetchQueries={refetchQueries}
          isPersonActive={isPersonActive}
          onSuspensionMutationSuccess={onSuspensionMutationSuccess}
        />
      )}

      {selectedSuspension && (
        <EditSuspensionModal
          open={editSuspensionModalOpen}
          onClose={handleEditModalClose}
          suspension={selectedSuspension}
          refetchQueries={refetchQueries}
          otherActiveSuspensionCount={otherActiveSuspensionCount}
          isSelectedSuspensionActive={isSelectedSuspensionActive}
          onSuspensionMutationSuccess={onSuspensionMutationSuccess}
        />
      )}

      {selectedSuspension && (
        <DeleteSuspensionModal
          open={deleteSuspensionModalOpen}
          onClose={handleDeleteModalClose}
          suspensionId={selectedSuspension.id}
          refetchQueries={refetchQueries}
          onSuspensionMutationSuccess={onSuspensionMutationSuccess}
        />
      )}

      {addNoteSuspensionId && (
        <AddEditNoteSideSheet
          open={!!addNoteSuspensionId}
          onClose={() => setAddNoteSuspensionId(null)}
          notableId={addNoteSuspensionId}
          notableType={NoteNotableValues.MEMBERSHIP_SUSPENSION}
          refetchQueries={refetchQueries}
          currentUserId={currentUserId}
          pgaId={pgaId}
          objectType="person"
        />
      )}

      <Menu />
    </div>
  );
}
